import { useCallback, useEffect, useRef } from 'preact/hooks';
import type { TimeSlot } from '../useDateTimePicker.js';

interface TimeSelectProps {
  slots: TimeSlot[];
  selectedHour: number;
  selectedMinute: number;
  onSelect: (hour: number, minute: number) => void;
}

// A snapping wheel of the step-boundary time slots (default 30-min), one value
// under a fixed centre band. Out-of-window / out-of-bounds slots render disabled,
// exactly as they did in the 4-column grid this replaces. Carii app dark-theme tokens.
//
// 093: the VALUES are deliberately unchanged — still 48 <button> elements labelled
// "HH:MM", still `disabled`, still selected by click. Only their container moved.
// That is what keeps the slot-label call sites in six other suites working, and it
// is why no explicit `role` appears below: an explicit role hides the implicit
// `button` one and `getByRole('button', {name: '14:30'})` would stop resolving.

/** Row height in px. MUST match the `h-10` on each value and on the band. */
const ITEM_H = 40;
/** Rows visible at once. Odd, so exactly one row is the centre. */
const VISIBLE = 5;
const COLUMN_H = ITEM_H * VISIBLE;
/**
 * Half a column minus half a row, above and below the values, so the FIRST and
 * LAST values can reach the centre band. Without it `00:00` and `23:30` are
 * unreachable — the wheel would stop with them at the column's edge.
 */
const PAD = (COLUMN_H - ITEM_H) / 2;

/**
 * With that padding, value `i` is centred exactly when `scrollTop === i * ITEM_H`:
 *   PAD + i*ITEM_H + ITEM_H/2  ===  scrollTop + COLUMN_H/2
 * The band's position is the contract; this is the arithmetic that honours it.
 */
const topFor = (index: number) => index * ITEM_H;

/** `scrollend` is not universally available and this app pins no browserslist and
 *  sets no vite `target`, so the debounced `scroll` fallback is required, not optional. */
const SETTLE_MS = 120;

export function TimeSelect({ slots, selectedHour, selectedMinute, onSelect }: TimeSelectProps) {
  const columnRef = useRef<HTMLDivElement | null>(null);
  /** Set while WE move the column, so our own scroll does not read as the user's. */
  const programmatic = useRef(false);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const releaseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedIndex = slots.findIndex(
    (s) => s.hour === selectedHour && s.minute === selectedMinute,
  );

  const scrollToIndex = useCallback((index: number, smooth: boolean) => {
    const col = columnRef.current;
    if (!col || index < 0) return;
    programmatic.current = true;
    const top = topFor(index);
    // jsdom implements neither scrollTo nor scrolling; the assignment fallback
    // keeps this a no-op there rather than a crash.
    if (typeof col.scrollTo === 'function') {
      col.scrollTo({ top, behavior: smooth ? 'smooth' : 'auto' });
    } else {
      col.scrollTop = top;
    }
    // Release on the next settle rather than immediately: a smooth scroll emits
    // its events after this call returns. Cancel any still-pending release from
    // an earlier call first — otherwise a rapid re-selection's stale timer can
    // clear the guard while THIS scroll is still in flight (#289).
    if (releaseTimer.current) clearTimeout(releaseTimer.current);
    releaseTimer.current = setTimeout(() => {
      programmatic.current = false;
    }, SETTLE_MS * 2);
  }, []);

  /**
   * Nearest selectable value to `from`, ties resolving UPWARD (later time first).
   * Returns -1 when the whole day is unavailable — the return picker on the
   * pickup's own day, where the minimum duration rules out every slot. In that
   * case the wheel must show that state rather than hunt for a target, which is
   * why FR-006 says an unavailable value must not be REPORTABLE, not merely
   * "not selected".
   */
  const nearestEnabled = useCallback(
    (from: number): number => {
      if (slots[from] && !slots[from].disabled) return from;
      for (let d = 1; d < slots.length; d++) {
        const up = from + d;
        if (slots[up] && !slots[up].disabled) return up;
        const down = from - d;
        if (slots[down] && !slots[down].disabled) return down;
      }
      return -1;
    },
    [slots],
  );

  /** The value the band is over, right now. */
  const indexUnderBand = useCallback(() => {
    const col = columnRef.current;
    if (!col) return -1;
    const raw = Math.round(col.scrollTop / ITEM_H);
    return Math.max(0, Math.min(slots.length - 1, raw));
  }, [slots.length]);

  const commitSettled = useCallback(() => {
    if (programmatic.current) return;
    const landed = indexUnderBand();
    if (landed < 0) return;
    const target = nearestEnabled(landed);
    // Whole day unavailable: leave the wheel where it is, report nothing.
    if (target < 0) return;
    // Landed on an unavailable value: correct in ONE hop. `target` is always
    // enabled, so the scroll this triggers cannot start the search again.
    if (target !== landed) {
      scrollToIndex(target, true);
    }
    const slot = slots[target];
    if (slot && (slot.hour !== selectedHour || slot.minute !== selectedMinute)) {
      onSelect(slot.hour, slot.minute);
    }
  }, [
    indexUnderBand,
    nearestEnabled,
    scrollToIndex,
    slots,
    selectedHour,
    selectedMinute,
    onSelect,
  ]);

  const handleScroll = useCallback(() => {
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(commitSettled, SETTLE_MS);
  }, [commitSettled]);

  // `scrollend` where the browser has it; the debounced `scroll` above is the
  // fallback and stays armed either way (a doubled commit is idempotent).
  useEffect(() => {
    const col = columnRef.current;
    if (!col || !('onscrollend' in col)) return;
    const onEnd = () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
      commitSettled();
    };
    col.addEventListener('scrollend', onEnd);
    return () => col.removeEventListener('scrollend', onEnd);
  }, [commitSettled]);

  useEffect(
    () => () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
      if (releaseTimer.current) clearTimeout(releaseTimer.current);
    },
    [],
  );

  // Bring the selection under the band whenever it changes from outside the
  // wheel — opening the picker, picking a day that re-floors the slots, or a
  // click on a visible value. Without this the band and the reported value can
  // disagree, which is the one failure FR-002 forbids outright.
  // biome-ignore lint/correctness/useExhaustiveDependencies: scrollToIndex is stable and re-running on it would fight the user's own scroll
  useEffect(() => {
    if (selectedIndex < 0) return;
    const col = columnRef.current;
    if (col && Math.round(col.scrollTop / ITEM_H) === selectedIndex) return;
    scrollToIndex(selectedIndex, false);
  }, [selectedIndex]);

  return (
    <div className="mt-3 pt-3 border-t border-border">
      <span className="block text-[10px] text-text-muted uppercase tracking-wider mb-1.5">
        Time
      </span>

      <div className="relative" style={{ height: `${COLUMN_H}px` }}>
        {/* The band is the definition of "selected", so it must never take the
            pointer: a tap aimed at the value under it has to reach the value.
            Measured in 092's sibling defect — a decorative overlay at the same
            z-index swallows the tap and no rectangle check can see it. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 z-10 h-10 border-y border-border bg-surface-mid/40"
          style={{ top: `${PAD}px` }}
        />

        <div
          ref={columnRef}
          onScroll={handleScroll}
          data-testid="time-wheel"
          className="h-full overflow-y-auto snap-y snap-mandatory overscroll-contain"
          // scrollbarWidth is inline rather than a utility class: this app has no
          // scrollbar-hiding utility and Principle V forbids inventing design
          // tokens. It is behaviour, not a token.
          style={{ paddingTop: `${PAD}px`, paddingBottom: `${PAD}px`, scrollbarWidth: 'none' }}
        >
          {slots.map((slot, index) => {
            const isSelected = slot.hour === selectedHour && slot.minute === selectedMinute;
            return (
              <button
                key={slot.label}
                type="button"
                disabled={slot.disabled}
                onClick={() => {
                  onSelect(slot.hour, slot.minute);
                  scrollToIndex(index, true);
                }}
                // 217 (#311): a mouse click focuses the button before `onClick` fires, which lets
                // the BROWSER's own uncontrolled scroll-into-view start racing our own
                // `onFocus`-triggered scroll below — a third, unguarded actor a pure scroll never
                // has. `preventDefault()` on `mousedown` stops the browser from focusing the button
                // via a mouse interaction at all (the `click` event itself is unaffected — it still
                // fires on `mouseup`), so a mouse click is left with exactly the one scroll `onClick`
                // itself starts. Keyboard focus (Tab) is untouched, so `onFocus`'s own centring
                // scroll still runs correctly there.
                onMouseDown={(e) => e.preventDefault()}
                onFocus={() => scrollToIndex(index, true)}
                className={[
                  'h-10 w-full snap-center flex items-center justify-center',
                  'text-sm font-medium tabular-nums',
                  slot.disabled
                    ? 'opacity-25 cursor-not-allowed text-text-muted'
                    : isSelected
                      ? 'text-text cursor-pointer'
                      : 'text-text-muted cursor-pointer hover:text-text',
                ].join(' ')}
              >
                {slot.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
